import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy, FilePdf, ChatCircle, Star } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import NotationDCE from './NotationDCE';
import EntrepriseReponseDetailModal from './EntrepriseReponseDetailModal';
import VoteAttributionPanel, { VoteRecapForLot } from './VoteAttributionPanel';
import usePermissions from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// A6 — montants au format français ; « Lot 03 » (P2#16)
const eur = (n) => (n === null || n === undefined ? '—'
  : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`);
const pct = (v) => (v === null || v === undefined ? '—'
  : `${v > 0 ? '+' : ''}${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);
const libLot = (numero, nom) => {
  const n = String(numero || '');
  return n ? `${/^\d+$/.test(n) ? `Lot ${n}` : n} — ${nom || ''}` : (nom || '');
};

// Statuts de DCE pour lesquels on n'attribue plus : le comparatif reste consultable
// (rapport pour le MOA) mais sans boutons d'attribution ni vote (CSL-07).
const DCE_STATUTS_LECTURE = ['ATTRIBUEE', 'ANNULE', 'CLOTURE', 'CLOTUREE'];

/** Clé d'une colonne d'offre : l'invitation (multi-invitations) sinon l'entreprise. */
const colKey = (o) => o.invitation_id || o.entreprise_id;

/**
 * Colonnes du comparatif d'un lot : une par offre (invitation), collectées sur
 * TOUS les postes (et non le seul 1er poste) pour que les cellules restent alignées.
 */
function buildColonnes(lotData) {
  const seen = new Map();
  (lotData.postes || []).forEach(p => (p.offres || []).forEach(o => {
    const k = colKey(o);
    if (k && !seen.has(k)) seen.set(k, { key: k, id: o.entreprise_id, nom: o.entreprise_nom, invitation_id: o.invitation_id });
  }));
  const cols = [...seen.values()];
  // Même entreprise invitée plusieurs fois : distinguer les colonnes
  const parEnt = {};
  cols.forEach(c => { parEnt[c.id] = (parEnt[c.id] || 0) + 1; });
  const rang = {};
  cols.forEach(c => {
    if (parEnt[c.id] > 1) {
      rang[c.id] = (rang[c.id] || 0) + 1;
      c.label = `${c.nom} (offre ${rang[c.id]})`;
    } else {
      c.label = c.nom;
    }
  });
  return cols;
}

/**
 * Comparatif des offres d'une consultation DCE (détail poste par poste + notation).
 *
 * Utilisé par DCEManager (DCE courant), par l'historique des DCE (« Consulter »)
 * et par le hub Marchés (« Voir comparatif DCE ») — CSL-04 / CSL-07.
 */
export default function DCEComparatifDialog({
  chantierId, dceId, dceStatut, open, onOpenChange, onAttribue, focusLotId,
}) {
  const { user: currentUser } = useAuth();
  const { role_projet: userRoleProjet } = usePermissions(chantierId);
  const [comparatif, setComparatif] = useState(null);
  const [tab, setTab] = useState('detail');
  const [detailEnt, setDetailEnt] = useState(null);
  // A6 (P2#11) — attribution avec confirmation
  const [aConfirmer, setAConfirmer] = useState(null); // { lotId, lotLib, entrepriseId, invitationId, nom, montant }
  const [attribution, setAttribution] = useState(false);
  const lectureSeule = DCE_STATUTS_LECTURE.includes(dceStatut);
  // R5 — l'AMO lit le rapport et donne son avis : ni notation, ni commentaires de
  // l'architecte modifiables, ni attribution (réservés à l'équipe de maîtrise d'œuvre).
  const estMoe = !userRoleProjet || ['archi', 'moe_principal', 'moe_membre'].includes(userRoleProjet);

  const load = useCallback(async () => {
    if (!dceId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/comparatif`, { withCredentials: true });
      setComparatif(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur chargement comparatif');
    }
  }, [chantierId, dceId]);

  useEffect(() => {
    if (open) { setTab('detail'); load(); } else { setComparatif(null); }
  }, [open, load]);

  // Hub Marchés : amener le lot demandé à l'écran
  useEffect(() => {
    if (!open || !comparatif || !focusLotId) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="comparatif-lot-${focusLotId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(t);
  }, [open, comparatif, focusLotId]);

  // Ouvre la confirmation « Attribuer le lot X à Y pour Z € HT ? »
  const demanderAttribution = (lotId, entrepriseId, invitationId) => {
    const lot = comparatif?.lots?.[lotId] || {};
    const offres = comparatif?.syntheses?.[lotId]?.offres || [];
    const o = offres.find((x) => (invitationId ? x.invitation_id === invitationId : x.entreprise_id === entrepriseId))
      || offres.find((x) => x.entreprise_id === entrepriseId);
    setAConfirmer({
      lotId, entrepriseId, invitationId: invitationId || o?.invitation_id || null,
      lotLib: libLot(lot.numero, lot.lot_nom),
      nom: o?.entreprise_nom || comparatif?.entreprises_noms?.[entrepriseId] || 'cette entreprise',
      montant: o?.total_ht,
      autres: Math.max(0, offres.length - 1),
    });
  };

  const handleAttribuer = async (lotId, entrepriseId, invitationId) => {
    setAttribution(true);
    try {
      const payload = { lot_id: lotId, entreprise_id: entrepriseId };
      // CSL-03 — l'invitation précise l'offre retenue quand l'entreprise a plusieurs invitations
      if (invitationId) payload.invitation_id = invitationId;
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/attribuer`,
        payload,
        { withCredentials: true }
      );
      toast.success(data.message);
      setAConfirmer(null);
      onAttribue?.();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur attribution');
    } finally { setAttribution(false); }
  };

  const handleSaveCommentaire = async (lotId, entrepriseId, commentaire) => {
    try {
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/commentaires`,
        { lot_id: lotId, entreprise_id: entrepriseId, commentaire },
        { withCredentials: true }
      );
      setComparatif(prev => {
        if (!prev) return prev;
        const next = { ...prev, commentaires: { ...(prev.commentaires || {}) } };
        next.commentaires[lotId] = { ...(next.commentaires[lotId] || {}), [entrepriseId]: commentaire };
        return next;
      });
    } catch {
      toast.error('Erreur sauvegarde commentaire');
    }
  };

  const handleExportPDF = () => {
    if (!dceId) return;
    window.open(`${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/comparatif/export-pdf`, '_blank');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="dce-comparatif-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
                Comparatif des offres
                {lectureSeule && (
                  <span className="ml-2 text-xs font-sans font-semibold uppercase tracking-wider text-[#6B6B6B]" data-testid="comparatif-lecture-seule">
                    · consultation terminée (lecture)
                  </span>
                )}
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none gap-2 border-[#C75B39] text-[#C75B39] mr-8"
                onClick={handleExportPDF}
                data-testid="comparatif-export-pdf"
              >
                <FilePdf size={14} /> Rapport d'analyse (PDF)
              </Button>
            </div>
          </DialogHeader>

          {/* Onglets */}
          <div className="flex gap-0 border-b border-[#E0E0DB] -mt-2 mb-3" data-testid="comparatif-tabs">
            <button
              onClick={() => setTab('detail')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'detail' ? 'border-[#C75B39] text-[#C75B39]' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'
              }`}
              data-testid="comparatif-tab-detail"
            >
              <ChatCircle size={14} className="inline mr-1.5" /> Détail des offres
            </button>
            {estMoe && (
            <button
              onClick={() => setTab('notation')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'notation' ? 'border-[#C75B39] text-[#C75B39]' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'
              }`}
              data-testid="comparatif-tab-notation"
            >
              <Star size={14} className="inline mr-1.5" /> Notation multicritère
            </button>
            )}
          </div>

          {tab === 'notation' && dceId && estMoe && (
            <NotationDCE
              chantierId={chantierId}
              dceId={dceId}
              onAttribuer={lectureSeule ? null : (lotId, entId) => demanderAttribution(lotId, entId)}
            />
          )}

          {tab === 'detail' && !comparatif && (
            <p className="p-6 text-sm text-[#6B6B6B]">Chargement du comparatif…</p>
          )}

          {tab === 'detail' && comparatif && (
            <div className="space-y-6">
              {Object.values(comparatif.lots || {}).map(lotData => {
                const attribution = comparatif.attributions?.[lotData.lot_id];
                const retenueId = attribution?.entreprise_id;
                const retenueInv = attribution?.invitation_id;
                const isRetenue = (c) => (retenueInv ? c.invitation_id === retenueInv : c.id === retenueId);
                const colonnes = buildColonnes(lotData);
                const synth = comparatif.syntheses?.[lotData.lot_id];
                // Commentaires et votes : par entreprise (clé historique côté backend)
                const entreprises = [...new Map(colonnes.map(c => [c.id, { id: c.id, nom: c.nom }])).values()];
                return (
                  <div
                    key={lotData.lot_id}
                    className={`border ${focusLotId === lotData.lot_id ? 'border-[#0369A1] ring-2 ring-[#0369A1]/30' : 'border-[#E0E0DB]'}`}
                    data-testid={`comparatif-lot-${lotData.lot_id}`}
                  >
                    <div className="bg-[#F5F5F3] px-4 py-3 flex items-center justify-between">
                      <span className="font-semibold text-[#2C2C2C]">{libLot(lotData.numero, lotData.lot_nom)}</span>
                      {attribution && <span className="text-xs px-2 py-0.5 bg-[#E8F5E9] text-[#2D6A4F] font-bold">Attribué</span>}
                    </div>
                    {/* A6 (P2#10) — totaux par entreprise, écart à l'estimation, délai */}
                    {synth && synth.offres.length > 0 && (
                      <div className="px-4 py-3 border-b border-[#E0E0DB] overflow-x-auto" data-testid={`comparatif-synthese-${lotData.lot_id}`}>
                        <p className="text-xs text-[#6B6B6B] mb-2">
                          Estimation de l'architecte : <strong>{eur(synth.estimation_ht)} HT</strong>
                          {' · '}{synth.nb_invitees} entreprise{synth.nb_invitees > 1 ? 's' : ''} invitée{synth.nb_invitees > 1 ? 's' : ''}
                          {' · '}{synth.nb_offres} offre{synth.nb_offres > 1 ? 's' : ''}
                        </p>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-[11px] uppercase text-[#6B6B6B]">
                              <th className="py-1 pr-3">Entreprise</th>
                              <th className="py-1 pr-3 text-right">Total HT</th>
                              <th className="py-1 pr-3 text-right">Écart à l'estimation</th>
                              <th className="py-1 text-right">Délai</th>
                            </tr>
                          </thead>
                          <tbody>
                            {synth.offres.map((o) => (
                              <tr key={o.invitation_id || o.entreprise_id} className="border-t border-[#F0F0EE]">
                                <td className="py-1 pr-3">
                                  {o.entreprise_nom}
                                  {o.moins_disant && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-[#E8F5E9] text-[#2D6A4F] font-bold uppercase">Moins-disant</span>}
                                  {o.hors_delai && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-[#FFEBEE] text-[#8B3A3A] font-bold" title="Offre envoyée après la date limite">hors délai</span>}
                                </td>
                                <td className="py-1 pr-3 text-right font-semibold">{eur(o.total_ht)}</td>
                                <td className={`py-1 pr-3 text-right ${o.ecart_estimation_pct > 0 ? 'text-[#C53030]' : 'text-[#2D6A4F]'}`}>{pct(o.ecart_estimation_pct)}</td>
                                <td className="py-1 text-right">{o.delai_jours ? `${o.delai_jours} jours` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {colonnes.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-[#9A9A9A]" data-testid={`comparatif-lot-vide-${lotData.lot_id}`}>
                        Aucune offre sur ce lot.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#E0E0DB] text-left text-xs text-[#6B6B6B] uppercase">
                              <th className="px-3 py-2">Poste</th>
                              <th className="px-3 py-2 text-center">Qté</th>
                              {colonnes.map(c => (
                                <th key={c.key} className={`px-3 py-2 text-right ${isRetenue(c) ? 'bg-[#E8F5E9] text-[#2D6A4F]' : ''}`}>
                                  <div>
                                    {c.label}
                                    {isRetenue(c) && <span className="ml-1">✓</span>}
                                  </div>
                                  <button
                                    onClick={() => setDetailEnt({ id: c.id, nom: c.nom, invitation_id: c.invitation_id })}
                                    className="text-[10px] font-semibold normal-case text-white bg-[#1E3A5F] hover:bg-[#152c46] px-2 py-1 mt-1 inline-flex items-center gap-1"
                                    data-testid={`comparatif-detail-${lotData.lot_id}-${c.id}`}
                                    title={`Voir le détail complet de l'offre de ${c.nom}`}
                                  >
                                    Voir le détail
                                  </button>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {lotData.postes?.map(poste => (
                              <tr key={poste.poste_id} className="border-b border-[#E0E0DB]">
                                <td className="px-3 py-2">{poste.designation}</td>
                                <td className="px-3 py-2 text-center text-[#6B6B6B]">{poste.quantite} {poste.unite}</td>
                                {colonnes.map(c => {
                                  const o = (poste.offres || []).find(x => colKey(x) === c.key);
                                  if (!o) return <td key={c.key} className="px-3 py-2 text-right text-[#9A9A9A]">—</td>;
                                  return (
                                    <td key={c.key} className={`px-3 py-2 text-right ${o.is_lowest ? 'font-bold text-[#2D6A4F] bg-[#E8F5E9]' : ''}`}>
                                      {Number(o.total_ht || 0).toLocaleString('fr-FR')} €
                                      <span className="block text-xs text-[#6B6B6B]">{Number(o.prix_unitaire_ht || 0).toLocaleString('fr-FR')} €/u</span>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Commentaires par entreprise */}
                    {entreprises.length > 0 && (
                      <div className="border-t border-[#E0E0DB] px-4 py-3 bg-[#FAFAF9]">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-2 flex items-center gap-1">
                          <ChatCircle size={12} /> Commentaires de l'architecte
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {entreprises.map(ent => (
                            <CommentaireInput
                              key={ent.id}
                              lotId={lotData.lot_id}
                              entrepriseId={ent.id}
                              entrepriseNom={ent.nom}
                              initial={comparatif.commentaires?.[lotData.lot_id]?.[ent.id] || ''}
                              onSave={handleSaveCommentaire}
                              isRetenue={ent.id === retenueId}
                              readOnly={!estMoe}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Récap des avis sur l'attribution (RP66) */}
                    <VoteRecapForLot votesLot={comparatif.votes?.[lotData.lot_id]} retenueId={retenueId} noms={comparatif.entreprises_noms} />

                    {/* Avis consultatif — seulement tant que la consultation est ouverte */}
                    {!lectureSeule && (
                      <VoteAttributionPanel
                        chantierId={chantierId}
                        dceId={dceId}
                        lotId={lotData.lot_id}
                        entreprises={entreprises}
                        currentUser={currentUser}
                        votesLot={comparatif.votes?.[lotData.lot_id]}
                        userRoleProjet={userRoleProjet}
                        onVoted={load}
                      />
                    )}

                    {/* Attribution : une option par offre (invitation) — CSL-03 */}
                    {estMoe && !lectureSeule && !attribution && colonnes.length > 0 && (
                      <div className="px-4 py-3 border-t border-[#E0E0DB] bg-[#FAFAF9]">
                        <p className="text-xs text-[#6B6B6B] mb-2">
                          Attribuer ce lot (décision finale de l'architecte, confirmation demandée) :
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {colonnes.map(c => (
                            <Button
                              key={c.key}
                              variant="outline"
                              size="sm"
                              className="rounded-none gap-1 text-xs"
                              onClick={() => demanderAttribution(lotData.lot_id, c.id, c.invitation_id)}
                              data-testid={`attribuer-${lotData.lot_id}-${c.id}`}
                            >
                              <Trophy size={12} />
                              {c.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* A6 (P2#11) — « Attribuer le lot X à Y pour Z € HT ? Les autres seront prévenues » */}
      <Dialog open={!!aConfirmer} onOpenChange={(o) => { if (!o) setAConfirmer(null); }}>
        <DialogContent className="max-w-md" data-testid="confirmer-attribution">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Attribuer le lot ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#2C2C2C]">
            Attribuer le <strong>{aConfirmer?.lotLib}</strong> à <strong>{aConfirmer?.nom}</strong>
            {aConfirmer?.montant ? <> pour <strong>{eur(aConfirmer.montant)} HT</strong></> : null} ?
          </p>
          <p className="text-xs text-[#6B6B6B]">
            {aConfirmer?.autres > 0
              ? `Les autres entreprises seront prévenues (${aConfirmer.autres} « non retenue »). `
              : 'Les autres entreprises seront prévenues. '}
            L'entreprise retenue reçoit l'avis d'attribution et accède au chantier.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setAConfirmer(null)}>Annuler</Button>
            <Button className="rounded-none bg-[#2D6A4F] hover:bg-[#225538] text-white gap-1" disabled={attribution}
              onClick={() => handleAttribuer(aConfirmer.lotId, aConfirmer.entrepriseId, aConfirmer.invitationId)}
              data-testid="confirmer-attribution-ok">
              <Trophy size={14} /> Attribuer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {detailEnt && dceId && (
        <EntrepriseReponseDetailModal
          chantierId={chantierId}
          dceId={dceId}
          entrepriseId={detailEnt.id}
          entrepriseNom={detailEnt.nom}
          invitationId={detailEnt.invitation_id}
          onClose={() => setDetailEnt(null)}
        />
      )}
    </>
  );
}

function CommentaireInput({ lotId, entrepriseId, entrepriseNom, initial, onSave, isRetenue, readOnly = false }) {
  const [value, setValue] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setValue(initial || ''); }, [initial]);
  const handleBlur = async () => {
    if (value === (initial || '')) return;
    setSaving(true);
    await onSave(lotId, entrepriseId, value);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };
  return (
    <div className={`border ${isRetenue ? 'border-[#2D6A4F]' : 'border-[#E0E0DB]'} bg-white`}>
      <div className={`text-[11px] px-2 py-1 font-semibold ${isRetenue ? 'bg-[#E8F5E9] text-[#2D6A4F]' : 'bg-[#F5F5F3] text-[#6B6B6B]'}`}>
        {entrepriseNom} {isRetenue && '· Retenue'}
      </div>
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        disabled={readOnly}
        rows={2}
        placeholder="Commentaire…"
        className="rounded-none border-0 text-xs resize-none focus-visible:ring-0"
        data-testid={`commentaire-${lotId}-${entrepriseId}`}
      />
      {saving && <p className="text-[10px] text-[#9A9A9A] px-2 pb-1">Enregistrement…</p>}
      {saved && <p className="text-[10px] text-[#2D6A4F] px-2 pb-1">✓ Enregistré</p>}
    </div>
  );
}
